/**
 * Language targeting API client — thin wrappers over the backend HTTP routes
 * mounted at `/api/v1/campaign-language-targeting` (feature #73).
 *
 * Endpoints used:
 *   POST /                   → create or upsert a rule set
 *   GET  /:campaignId        → read an existing rule set (404 → null)
 *   PUT  /:campaignId        → atomic replace of a rule set
 *   DELETE /:campaignId      → drop the rule set
 *
 * The dash form uses `upsertLanguageTargeting` (POST) as the single save
 * path, which creates the rule set if missing or replaces it otherwise.
 */

import { apiClient, ApiError } from "../../api-client";

export const LANGUAGE_TARGETING_PATH = "/campaign-language-targeting";

export const TARGETING_MODES = ["allow", "deny"] as const;
export type TargetingMode = (typeof TARGETING_MODES)[number];

export interface LanguageTargetingView {
	campaign_id: string;
	mode: TargetingMode;
	languages: string[];
	created_at: number;
	updated_at: number;
}

export interface UpsertLanguageTargetingInput {
	campaignId: string;
	mode: TargetingMode;
	languages: string[];
}

/**
 * Canonical ISO-639-1 language options supported by the targeting form.
 * Codes match those accepted by the API's `normaliseLanguageCode` helper
 * (lowercase two-letter primary tags).
 */
export interface LanguageOption {
	code: string;
	label: string;
	native: string;
}

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
	{ code: "en", label: "English", native: "English" },
	{ code: "es", label: "Spanish", native: "Espanol" },
	{ code: "pt", label: "Portuguese", native: "Portugues" },
	{ code: "ru", label: "Russian", native: "Russkiy" },
	{ code: "zh", label: "Chinese", native: "Zhongwen" },
	{ code: "de", label: "German", native: "Deutsch" },
	{ code: "fr", label: "French", native: "Francais" },
	{ code: "ar", label: "Arabic", native: "Arabi" },
	{ code: "hi", label: "Hindi", native: "Hindi" },
	{ code: "ja", label: "Japanese", native: "Nihongo" },
] as const;

export const LANGUAGE_CODE_SET: ReadonlySet<string> = new Set(
	LANGUAGE_OPTIONS.map((o) => o.code),
);

/**
 * Look up a language option by ISO-639-1 code. Unknown codes are returned
 * as a minimal option object so the UI can still render them.
 */
export function resolveLanguageOption(code: string): LanguageOption {
	const found = LANGUAGE_OPTIONS.find((o) => o.code === code.toLowerCase());
	if (found) return found;
	return { code: code.toLowerCase(), label: code.toUpperCase(), native: code.toUpperCase() };
}

/**
 * Upsert a rule set via POST. Returns the persisted view regardless of
 * whether a row already existed.
 */
export async function upsertLanguageTargeting(
	input: UpsertLanguageTargetingInput,
): Promise<LanguageTargetingView> {
	return apiClient.post<LanguageTargetingView>(LANGUAGE_TARGETING_PATH, input);
}

/**
 * Fetch a rule set for the given campaign. Returns `null` when no rule
 * set has been configured (API responds 404).
 */
export async function fetchLanguageTargeting(
	campaignId: string,
): Promise<LanguageTargetingView | null> {
	try {
		return await apiClient.get<LanguageTargetingView>(
			`${LANGUAGE_TARGETING_PATH}/${encodeURIComponent(campaignId)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

/**
 * Delete a rule set. Resolves silently on 404 so the form can treat the
 * "clear" action as idempotent.
 */
export async function deleteLanguageTargeting(campaignId: string): Promise<void> {
	try {
		await apiClient.delete(`${LANGUAGE_TARGETING_PATH}/${encodeURIComponent(campaignId)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export const languageTargetingQueryKey = (campaignId: string) =>
	["language-targeting", campaignId] as const;
